// src/pages/Home/Home.js
import { useRef, useCallback, useState, useEffect } from 'react';
import styles from './Home.module.css';
import logo from '../../assets/cpi/logo.png';
import ContactForm from '../../components/ContactForm/ContactForm';
import useScrollReveal, { useStaggerReveal } from '../../hooks/useScrollReveal';
import { RiWindowLine, RiBrainLine, RiSmartphoneLine, RiPuzzle2Line, RiRocket2Line, RiCpuLine } from '@remixicon/react';

// SVG logos kept as imports (tiny). All raster assets are loaded as compressed webp
// from /public/optimized/ to keep page weight low.
import variablyLogo from '../../assets/varibly/logo.svg';
import layovraLogo from '../../assets/layovra/logo.svg';
import autopersonasLogo from '../../assets/autopersonas/logo.svg';

const asset = (path) => `${process.env.PUBLIC_URL}/optimized/${path}`;

const clickcvLogo = asset('clickcv/logo.webp');
const clickcvScreen1 = asset('clickcv/screen1.webp');
const clickcvScreen2 = asset('clickcv/screen2.webp');
const spacedsaLogo = asset('spacedsa/logo.webp');
const spacedsaScreen1 = asset('spacedsa/screen1.webp');
const spacedsaScreen2 = asset('spacedsa/screen2.webp');
const promptgruupLogo = asset('promptgruup/logo.webp');
const promptgruupScreen = asset('promptgruup/screen1.webp');
const promptgruupScreen2 = asset('promptgruup/screen2.webp');
const promptgruupScreen3 = asset('promptgruup/screen3.webp');
const promptgruupScreen4 = asset('promptgruup/screen4.webp');
const subslockLogo = asset('subslock/logo.webp');
const subslockScreen = asset('subslock/screenshot.webp');
const variablyScreen1 = asset('varibly/screen1.webp');
const variablyScreen2 = asset('varibly/screen2.webp');
const layovraScreen1 = asset('layovra/screeenshot.webp');
const layovraScreen2 = asset('layovra/screenshot_1.webp');
const autopersonasScreen1 = asset('autopersonas/screenshot.webp');
const autopersonasScreen2 = asset('autopersonas/screenshot_2.webp');
const autopersonasScreen3 = asset('autopersonas/screenshot_3.webp');
const autopersonasScreen4 = asset('autopersonas/screenshot_4.webp');
const qwyttLogo = asset('qwytt/logo.webp');
const qwyttScreen2 = asset('qwytt/screen2.webp');
const qwyttScreen3 = asset('qwytt/screen3.webp');
const qwyttScreen4 = asset('qwytt/screen4.webp');
const qwyttScreen5 = asset('qwytt/screen5.webp');
const qwyttScreen6 = asset('qwytt/screen6.webp');

const services = [
  {
    title: 'Custom Web Applications',
    description:
      'Full-stack applications engineered for performance, scalability, and the user experience your customers expect. From internal tools to customer-facing platforms.',
    icon: <RiWindowLine size={36} color="#ae395d" />,
  },
  {
    title: 'AI & LLM Solutions',
    description:
      'Production-grade AI integrations that deliver measurable ROI. Intelligent automation, custom LLM workflows, computer vision, and API orchestration tailored to your business logic.',
    icon: <RiBrainLine size={36} color="#ae395d" />,
  },
  {
    title: 'Cross-Platform Mobile Apps',
    description:
      'Native-quality iOS and Android applications from a single codebase. Built with React Native for fast iteration, consistent UX across platforms, and lower long-term maintenance.',
    icon: <RiSmartphoneLine size={36} color="#ae395d" />,
  },
  {
    title: 'Browser Extensions & Tooling',
    description:
      'Chrome extensions and browser-based products that reach users where they already work. Built for reliability, security, and seamless distribution via the Chrome Web Store.',
    icon: <RiPuzzle2Line size={36} color="#ae395d" />,
  },
  {
    title: 'MVP & Product Development',
    description:
      'Go from validated idea to market-ready product. We handle architecture, design, and development so you can focus on growing your business and acquiring users.',
    icon: <RiRocket2Line size={36} color="#ae395d" />,
  },
  {
    title: 'Hardware Integrations',
    description:
      'Bridge the gap between software and physical devices. IoT sensors, Bluetooth peripherals, and embedded systems. We build the firmware and app layers that make them work together.',
    icon: <RiCpuLine size={36} color="#ae395d" />,
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Discovery',
    description:
      'We learn your goals, constraints, and users. Together we define the scope, timeline, and success criteria before any code is written.',
  },
  {
    step: '02',
    title: 'Architecture & Design',
    description:
      'We plan the technical approach, choose the right stack, and design intuitive interfaces so we build the right thing the first time.',
  },
  {
    step: '03',
    title: 'Build & Iterate',
    description:
      'Agile development with regular check-ins. You see working software every sprint, not just status updates.',
  },
  {
    step: '04',
    title: 'Launch & Support',
    description:
      'We deploy to production, monitor performance, and provide ongoing support to ensure long-term success.',
  },
];

const technologies = [
  { name: 'React', category: 'Frontend' },
  { name: 'Next.js', category: 'Frontend' },
  { name: 'JavaScript', category: 'Frontend' },
  { name: 'TypeScript', category: 'Frontend' },
  { name: 'HTML / CSS', category: 'Frontend' },
  { name: 'Node.js', category: 'Backend' },
  { name: 'Python', category: 'Backend' },
  { name: 'Firebase', category: 'Cloud' },
  { name: 'Flask', category: 'Backend' },
  { name: 'Fastify', category: 'Backend' },
  { name: 'REST APIs', category: 'Backend' },
  { name: 'OpenAI API', category: 'AI / ML' },
  { name: 'Anthropic API', category: 'AI / ML' },
  { name: 'Gemini API', category: 'AI / ML' },
  { name: 'Prompt Engineering', category: 'AI / ML' },
  { name: 'MCP', category: 'AI / ML' },
  { name: 'OpenCV', category: 'AI / ML' },
  { name: 'MediaPipe', category: 'AI / ML' },
  { name: 'YOLO', category: 'AI / ML' },
  { name: 'React Native', category: 'Mobile' },
  { name: 'Flutter', category: 'Mobile' },
  { name: 'SwiftUI', category: 'Mobile' },
  { name: 'UIKit', category: 'Mobile' },
  { name: 'Google Cloud', category: 'Cloud' },
];

const heroShowcase = [
  {
    label: 'Qwytt',
    format: 'mobile',
    images: [qwyttScreen2, qwyttScreen3, qwyttScreen4, qwyttScreen5, qwyttScreen6],
  },
  {
    label: 'AutoPersonas',
    images: [autopersonasScreen2, autopersonasScreen3, autopersonasScreen4, autopersonasScreen1],
  },
  {
    label: 'PromptGruup',
    images: [promptgruupScreen, promptgruupScreen2, promptgruupScreen3, promptgruupScreen4],
  },
  {
    label: 'ClickCV',
    images: [clickcvScreen2, clickcvScreen1],
  },
  {
    label: 'Layovra',
    images: [layovraScreen1, layovraScreen2],
  },
  {
    label: 'SpaceDSA',
    images: [spacedsaScreen1, spacedsaScreen2],
  },
  {
    label: 'Subslock',
    images: [subslockScreen],
  },
  {
    label: 'Varibly',
    images: [variablyScreen1, variablyScreen2],
  },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function HeroShowcase() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 5500);
    return () => clearInterval(id);
  }, []);

  const handleClick = (label) => {
    const target = document.getElementById(`portfolio-${slugify(label)}`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Duplicate the list for seamless marquee scroll
  const cards = [...heroShowcase, ...heroShowcase];

  return (
    <div className={styles.heroShowcase}>
      <div className={styles.heroShowcaseTrack}>
        {cards.map((item, i) => {
          const stagger = i % heroShowcase.length;
          const current = (tick + stagger) % item.images.length;
          const cardClass = item.format === 'mobile'
            ? `${styles.heroShowcaseCard} ${styles.heroShowcaseCardMobile}`
            : styles.heroShowcaseCard;
          return (
            <button
              key={`${item.label}-${i}`}
              type="button"
              className={cardClass}
              onClick={() => handleClick(item.label)}
              aria-label={`Jump to ${item.label} in portfolio`}
            >
              {item.images.map((src, j) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  loading={j === 0 ? 'eager' : 'lazy'}
                  decoding="async"
                  className={styles.heroShowcaseImg}
                  style={{ opacity: j === current ? 1 : 0 }}
                />
              ))}
              <span className={styles.heroShowcaseLabel}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const portfolio = [
  {
    name: 'Qwytt',
    tagline: 'Smart Nicotine Cessation Platform',
    description:
      'Mobile app and IoT sensor platform that helps users quit vaping through data-driven gradual reduction. A clip-on sensor automatically tracks puff activity while AI-powered algorithms generate personalized reduction goals that adapt in real time.',
    highlights: [
      'IoT Sensor Integration',
      'AI-Powered Goals',
      'Mobile App (iOS & Android)',
      'Firebase Backend',
    ],
    logo: qwyttLogo,
    logoBg: '#6254ba',
    layout: 'mobile',
    screenshots: [qwyttScreen2, qwyttScreen3, qwyttScreen4, qwyttScreen5, qwyttScreen6],
    link: 'https://www.qwytt.com',
  },
  {
    name: 'AutoPersonas',
    tagline: 'AI Social Media Management',
    description:
      'AI-powered platform for managing social media accounts and creating AI-generated characters across X/Twitter and Instagram. Schedule posts, track engagement, and automate content with persistent visual identities and human-in-the-loop approval.',
    highlights: [
      'Social Media Management',
      'AI Character Creation',
      'Multi-Platform Posting',
      'Content Scheduling',
      'MCP',
    ],
    logo: autopersonasLogo,
    layout: 'showcase',
    screenshots: [autopersonasScreen1, autopersonasScreen2, autopersonasScreen3, autopersonasScreen4],
    link: 'https://autopersonas.com',
  },
  {
    name: 'ClickCV',
    tagline: 'AI-Powered Resume Builder',
    description:
      'Chrome extension that generates tailored, ATS-friendly resumes on the fly. Integrates directly with major job boards to automatically restructure resumes based on each job description, saving users hours per application.',
    highlights: [
      'Chrome Extension',
      'AI / LLM Integration',
      'Cross-Site Compatibility',
    ],
    logo: clickcvLogo,
    layout: 'layered',
    screenshots: [clickcvScreen1, clickcvScreen2],
    link: 'https://clickcv.castlepointinnovations.com',
  },
  {
    name: 'SpaceDSA',
    tagline: 'Spaced Repetition Study Platform',
    description:
      'Study tool built on cognitive science that uses spaced repetition algorithms to help developers retain data structures and algorithm knowledge. Automatically schedules review sessions and tracks problem-solving performance over time.',
    highlights: [
      'Spaced Repetition Engine',
      'Progress Analytics',
      'Chrome Extension',
      'Web Scraping & DOM Integration',
    ],
    logo: spacedsaLogo,
    layout: 'layered',
    screenshots: [spacedsaScreen1, spacedsaScreen2],
    link: 'https://spacedsa.castlepointinnovations.com',
  },
  {
    name: 'Layovra',
    tagline: 'AI Furniture Discovery & Layout Platform',
    description:
      'Proof-of-concept furniture discovery and room planning platform. Users search for furniture with natural language, import room dimensions via LiDAR, and auto-generate optimized layouts with 3D visualization and multi-format exports.',
    highlights: [
      'Natural Language Search',
      'LiDAR Room Import',
      'AI Layout Generation',
      '3D Visualization',
    ],
    logo: layovraLogo,
    layout: 'layered',
    screenshots: [layovraScreen1, layovraScreen2],
    link: null,
  },
  {
    name: 'PromptGruup',
    tagline: 'Visual LLM Workflow Platform',
    description:
      'Proof-of-concept full-stack web application featuring a node-based interface for designing, testing, and deploying multi-step LLM workflows. Supports multiple AI providers with real-time collaboration and built-in version control.',
    highlights: [
      'Node-Based Editor',
      'Multi-LLM Support',
      'Real-Time Collaboration',
      'Workflow Version Control',
    ],
    logo: promptgruupLogo,
    layout: 'single',
    screenshots: [promptgruupScreen],
    link: 'https://promptgruup.com/',
  },
  {
    name: 'Subslock',
    tagline: 'Telegram Subscription Paywall',
    description:
      'Solana-based subscription platform enabling creators to set up paywalls for Telegram channels. Handles USDC payments, subscription management, and platform fees via on-chain smart contracts with a full-stack web dashboard.',
    highlights: [
      'Solana Smart Contracts',
      'Telegram Integration',
      'USDC Payments',
      'Subscription Management',
    ],
    logo: subslockLogo,
    layout: 'singleTall',
    screenshots: [subslockScreen],
    link: 'https://www.subslock.com',
  },
  {
    name: 'Varibly',
    tagline: 'Rapid Ad Creative Generator',
    description:
      'Social media creative generator that produces 50+ ad variants in minutes. Uses LLM-driven copy generation and HTML template rendering with support for multiple platforms including Instagram Stories and Facebook Feed.',
    highlights: [
      'Batch Ad Generation',
      'Multi-Platform Support',
      'LLM-Powered Copy',
      'Video Rendering',
      'MCP',
    ],
    logo: variablyLogo,
    layout: 'layered',
    screenshots: [variablyScreen2, variablyScreen1],
    link: 'https://varibly.com',
  },
];

function Home() {
  const [lightboxImg, setLightboxImg] = useState(null);

  // Close lightbox on Escape key
  useEffect(() => {
    if (!lightboxImg) return;
    const handleKey = (e) => { if (e.key === 'Escape') setLightboxImg(null); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxImg]);

  // Section header scroll reveals
  const servicesHeaderRef = useScrollReveal({ threshold: 0.2 });
  const processHeaderRef = useScrollReveal({ threshold: 0.2 });
  const techHeaderRef = useScrollReveal({ threshold: 0.2 });
  const portfolioHeaderRef = useScrollReveal({ threshold: 0.2 });
  const contactRef = useScrollReveal({ threshold: 0.15 });

  // Staggered reveals for grids
  const servicesGridRef = useStaggerReveal({ staggerMs: 100, selector: ':scope > div' });
  const processGridRef = useStaggerReveal({ staggerMs: 150, selector: ':scope > div' });
  const techGridRef = useStaggerReveal({ staggerMs: 50, selector: ':scope > div' });

  // Process grid also needs its own reveal for the connecting line
  const processLineRef = useScrollReveal({ threshold: 0.2 });

  // Portfolio card reveals (individual refs for each card)
  const portfolioRefs = useRef([]);
  const setPortfolioRef = useCallback((index) => (el) => {
    portfolioRefs.current[index] = el;
  }, []);

  // Set up portfolio card observers
  const portfolioObserverRef = useRef(null);
  const portfolioContainerRef = useCallback((node) => {
    if (portfolioObserverRef.current) {
      portfolioObserverRef.current.disconnect();
    }

    if (!node) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cards = node.querySelectorAll('[data-portfolio-card]');

    if (mq.matches) {
      cards.forEach((card) => card.setAttribute('data-revealed', 'true'));
      return;
    }

    portfolioObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', 'true');
            portfolioObserverRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    cards.forEach((card) => portfolioObserverRef.current.observe(card));
  }, []);

  // Merge processGridRef and processLineRef
  const mergedProcessRef = useCallback((node) => {
    processGridRef.current = node;
    processLineRef.current = node;
  }, [processGridRef, processLineRef]);

  return (
    <div className={styles.home}>
      {/* HERO */}
      <header className={styles.hero} id="home">
        {/* Floating particles */}
        <div className={styles.heroParticle} />
        <div className={styles.heroParticle} />
        <div className={styles.heroParticle} />
        <div className={styles.heroParticle} />
        <div className={styles.heroParticle} />

        <img
          src={logo}
          alt="Castle Point Innovations"
          className={styles.heroLogo}
        />
        <h1>Ambitious software, shipped on a deadline.</h1>
        <p className={styles.heroSubtitle}>
          Web, mobile, AI, browser extensions, and hardware integrations.
          Engineered carefully, shipped on schedule, owned by one team from
          first prototype to production.
        </p>
        <div className={styles.heroCtas}>
          <a href="#contact" className={styles.heroCta}>
            Start a Project
          </a>
          <a href="#portfolio" className={styles.heroCtaSecondary}>
            See Our Work
          </a>
        </div>

        {/* Showcase marquee — auto-scrolling app screenshots that fade-cycle */}
        <HeroShowcase />
      </header>

      {/* SERVICES */}
      <section className={styles.section} id="services">
        <div ref={servicesHeaderRef}>
          <h2 className={styles.sectionTitle}>What We Build</h2>
          <p className={styles.sectionSubtitle}>
            Full-stack engineering across web, mobile, and AI so you ship faster with fewer vendors.
          </p>
        </div>
        <div className={styles.servicesGrid} ref={servicesGridRef}>
          {services.map((service) => (
            <div key={service.title} className={styles.serviceCard}>
              <span className={styles.serviceIcon}>{service.icon}</span>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className={styles.sectionAlt} id="process">
        <div ref={processHeaderRef}>
          <h2 className={styles.sectionTitle}>How We Work</h2>
          <p className={styles.sectionSubtitle}>
            A clear process designed to minimize risk and maximize value at every
            stage.
          </p>
        </div>
        <div className={styles.processGrid} ref={mergedProcessRef}>
          {processSteps.map((item) => (
            <div key={item.step} className={styles.processCard}>
              <span className={styles.processStep}>{item.step}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TECHNOLOGIES */}
      <section className={styles.section} id="technologies">
        <div ref={techHeaderRef}>
          <h2 className={styles.sectionTitle}>Technologies We Work With</h2>
          <p className={styles.sectionSubtitle}>
            Modern, battle-tested tools chosen for reliability and long-term
            maintainability.
          </p>
        </div>
        <div className={styles.techGrid} ref={techGridRef}>
          {technologies.map((tech) => (
            <div key={tech.name} className={styles.techBadge}>
              <span className={styles.techName}>{tech.name}</span>
              <span className={styles.techCategory}>{tech.category}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className={styles.sectionAlt} id="portfolio">
        <div ref={portfolioHeaderRef}>
          <h2 className={styles.sectionTitle}>Selected Work</h2>
          <p className={styles.sectionSubtitle}>
            Products we designed, engineered, and shipped to production.
          </p>
        </div>
        <div className={styles.portfolioList} ref={portfolioContainerRef}>
          {(() => {
            const isFlagship = (layout) => layout === 'mobile' || layout === 'showcase';
            let sideBySideCounter = 0;
            return portfolio.map((project, index) => {
              const layoutClass = {
                mobile: styles.layoutMobile,
                showcase: styles.layoutShowcase,
                layered: styles.layoutLayered,
                single: styles.layoutSingle,
                singleTall: styles.layoutSingleTall,
              }[project.layout];
              const flagship = isFlagship(project.layout);
              const reversed = !flagship && sideBySideCounter % 2 !== 0;
              if (!flagship) sideBySideCounter++;
              return (
                <div
                  key={project.name}
                  id={`portfolio-${slugify(project.name)}`}
                  data-portfolio-card
                  ref={setPortfolioRef(index)}
                  className={`${styles.portfolioCard} ${layoutClass} ${
                    reversed ? styles.portfolioCardReversed : ''
                  }`}
                >
                  <div className={styles.portfolioInfo}>
                    <div className={styles.portfolioHeader}>
                      <img
                        src={project.logo}
                        alt={`${project.name} logo`}
                        className={styles.portfolioLogo}
                        style={project.logoBg ? { background: project.logoBg, borderRadius: '12px', padding: '6px' } : undefined}
                      />
                      <div>
                        <h3>{project.name}</h3>
                        <span className={styles.portfolioTagline}>
                          {project.tagline}
                        </span>
                      </div>
                    </div>
                    <p>{project.description}</p>
                    <div className={styles.portfolioHighlights}>
                      {project.highlights.map((h) => (
                        <span key={h} className={styles.highlightTag}>
                          {h}
                        </span>
                      ))}
                    </div>
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.portfolioLink}
                      >
                        View Live Project &rarr;
                      </a>
                    )}
                  </div>
                  <div className={styles.portfolioVisual}>
                    {project.screenshots.map((src, i) => (
                      <div
                        key={i}
                        className={styles.portfolioScreenshot}
                        onClick={() => setLightboxImg({ src, alt: `${project.name} screenshot ${i + 1}` })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') setLightboxImg({ src, alt: `${project.name} screenshot ${i + 1}` }); }}
                      >
                        <img src={src} alt={`${project.name} screenshot ${i + 1}`} loading="lazy" decoding="async" />
                        <span className={styles.expandIcon}>&#x26F6;</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </section>

      {/* CONTACT */}
      <section className={styles.contactSection} id="contact" ref={contactRef}>
        <h2>Let's Build Something Together</h2>
        <p>
          Have a project in mind? Fill out the form below and we'll get back to
          you within 24 hours to discuss your goals and how we can help.
        </p>
        <ContactForm />
        <p className={styles.contactEmail}>
          or email us directly at contact@castlepointinnovations.com
        </p>
      </section>

      {/* LIGHTBOX */}
      {lightboxImg && (
        <div
          className={styles.lightboxOverlay}
          onClick={() => setLightboxImg(null)}
          role="dialog"
          aria-label="Expanded screenshot"
        >
          <button
            className={styles.lightboxClose}
            onClick={() => setLightboxImg(null)}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={lightboxImg.src}
            alt={lightboxImg.alt}
            className={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default Home;
